// Small helper wrapping Meta Pixel + AppLovin AXON pixel calls used across the site.
// Both pixel stubs are installed globally in /app/frontend/public/index.html.
// They're *activated* inside useMetaPixel() as soon as the backend /api/config
// returns the respective IDs/keys.

let _pixelInited = false;
let _pixelId = null;
let _axonInited = false;

export function initPixel(id) {
  if (!id || _pixelInited) return;
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  _pixelId = id;
  _pixelInited = true;
  try {
    window.fbq("init", id);
  } catch (_) { /* ignore */ }
}

/**
 * AppLovin AXON pixel is initialised directly in /app/frontend/public/index.html
 * (per AppLovin's official snippet — the event key must be on the stub before
 * pixel.js loads). This hook exists for API symmetry with initPixel and is a no-op.
 */
export function initAxon(_eventKey) {
  _axonInited = true;
}

function fbqAvailable() {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

function axonAvailable() {
  return typeof window !== "undefined" && typeof window.axon === "function";
}

function safe(cb) {
  try { cb(); } catch (_) { /* ignore */ }
}

/** Fires standard PageView. Safe to call on every route change. */
export function trackPageView() {
  safe(() => {
    if (fbqAvailable()) window.fbq("track", "PageView");
  });
  safe(() => {
    if (axonAvailable()) window.axon("track", "page_view");
  });
}

/** Fired when a visitor views a product detail page. Feeds Meta retargeting. */
export function trackViewContent(product) {
  if (!product) return;
  safe(() => {
    if (!fbqAvailable()) return;
    window.fbq("track", "ViewContent", {
      content_ids: [product.slug || product.id],
      content_name: product.name,
      content_type: "product",
      content_category: product.category || undefined,
    });
  });
  safe(() => {
    if (!axonAvailable()) return;
    window.axon("track", "view_item", {
      items: [{
        item_id: product.slug || product.id,
        item_name: product.name,
        item_category: product.category || undefined,
      }],
    });
  });
}

/**
 * Fired when a visitor clicks the "Open in Amazon App" CTA.
 * Sends BOTH a standard AddToCart (best for ad optimization — Meta's algorithm
 * has years of training data on it) and a custom ClickAmazon (for clean
 * reporting in Events Manager).
 */
export function trackAmazonClick(product) {
  if (!product) return;
  safe(() => {
    if (!fbqAvailable()) return;
    const payload = {
      content_ids: [product.slug || product.id],
      content_name: product.name,
      content_type: "product",
      content_category: product.category || undefined,
    };
    window.fbq("track", "AddToCart", payload);
    window.fbq("trackCustom", "ClickAmazon", payload);
  });
  safe(() => {
    if (!axonAvailable()) return;
    window.axon("track", "add_to_cart", {
      items: [{
        item_id: product.slug || product.id,
        item_name: product.name,
        item_category: product.category || undefined,
        quantity: 1,
      }],
    });
  });
}

/** Fired when a visitor submits an email. */
export function trackLead({ email, contentName }) {
  safe(() => {
    if (!fbqAvailable()) return;
    window.fbq("track", "Lead", {
      content_name: contentName || "Subscribe",
      email,
    });
  });
  safe(() => {
    if (!axonAvailable()) return;
    window.axon("track", "generate_lead", {
      content_name: contentName || "Subscribe",
      email,
    });
  });
}
